import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { ChevronDown, CircleAlert, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'
import { whatsappLink } from '@/constants/site'

interface FieldProps {
    id: string
    label: string
    required?: boolean
    error?: string
    hint?: string
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({
    id,
    label,
    required = false,
    error,
    hint,
    className,
    children
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <label htmlFor={id} className='text-[13px] font-semibold text-brand-charcoal'>
                {label}
                {required
                    ? <span aria-hidden='true' className='text-brand-ember'> *</span>
                    : <span className='font-normal text-brand-stone'> (optional)</span>}
            </label>

            {children}

            {error
                ? (
                    <p id={`${id}-error`} className='flex items-start gap-1.5 text-[13px] font-medium text-destructive'>
                        <CircleAlert className='mt-0.5 size-3.5 shrink-0' aria-hidden='true' />
                        {error}
                    </p>
                )
                : hint && (
                    <p className='text-xs leading-relaxed text-brand-stone'>
                        {hint}
                    </p>
                )}
        </div>
    )
}

const controlStyles = (invalid: boolean, className?: string): string => cn(
    'w-full rounded-xl border bg-white px-4 text-[15px] text-brand-charcoal transition-[border-color,box-shadow] duration-200 placeholder:text-brand-stone/55 focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/15 focus:outline-none disabled:opacity-60',
    invalid ? 'border-destructive focus:border-destructive focus:ring-destructive/15' : 'border-input hover:border-brand-stone/40',
    className
)

type TextInputProps = InputHTMLAttributes<HTMLInputElement>

export const TextInput: FC<TextInputProps> = ({ className, ...props }): ReactNode => {
    return (
        <input
            {...props}
            className={controlStyles(Boolean(props['aria-invalid']), cn('h-12', className))}
        />
    )
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export const TextArea: FC<TextAreaProps> = ({ className, ...props }): ReactNode => {
    return (
        <textarea
            rows={4}
            {...props}
            className={controlStyles(Boolean(props['aria-invalid']), cn('min-h-28 resize-y py-3 leading-relaxed', className))}
        />
    )
}

type SelectOption = string | { value: string, label: string }

export type SelectGroup = {
    label: string
    options: Array<SelectOption>
}

interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
    placeholder?: string
    options?: Array<SelectOption>
    groups?: Array<SelectGroup>
}

const renderOption = (option: SelectOption): ReactNode => {
    const value = typeof option === 'string' ? option : option.value
    const label = typeof option === 'string' ? option : option.label

    return (
        <option key={value} value={value} className='text-brand-charcoal'>
            {label}
        </option>
    )
}

export const SelectInput: FC<SelectInputProps> = ({
    className,
    placeholder,
    options = [],
    groups = [],
    ...props
}): ReactNode => {
    return (
        <div className='relative'>
            <select
                {...props}
                className={controlStyles(Boolean(props['aria-invalid']), cn('h-12 cursor-pointer appearance-none pr-11', !props.value && 'text-brand-stone/80', className))}
            >
                {placeholder && (
                    <option value=''>
                        {placeholder}
                    </option>
                )}

                {groups.map(group => (
                    <optgroup key={group.label} label={group.label}>
                        {group.options.map(renderOption)}
                    </optgroup>
                ))}

                {options.map(renderOption)}
            </select>

            <ChevronDown className='pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-brand-stone' aria-hidden='true' />
        </div>
    )
}

export const Honeypot: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='pointer-events-none absolute -left-[9999px] size-px overflow-hidden opacity-0'>
            <label>
                Company website
                <input type='text' name='company_website' tabIndex={-1} autoComplete='off' defaultValue='' />
            </label>
        </div>
    )
}

interface SubmitButtonProps {
    submitting: boolean
    label: string
    submittingLabel?: string
    className?: string
}

export const SubmitButton: FC<SubmitButtonProps> = ({
    submitting,
    label,
    submittingLabel = 'Sending',
    className
}): ReactNode => {
    return (
        <button
            type='submit'
            disabled={submitting}
            aria-disabled={submitting}
            className={buttonStyles({ variant: 'primary', size: 'lg', className: cn('w-full sm:w-auto', className) })}
        >
            {submitting && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
            {submitting ? submittingLabel : label}
        </button>
    )
}

interface FormAlertProps {
    message: string | null
}

export const FormAlert: FC<FormAlertProps> = ({ message }): ReactNode => {
    if (!message) return null

    return (
        <div role='alert' className='flex flex-col gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 p-4 text-sm text-brand-charcoal sm:flex-row sm:items-center sm:justify-between'>
            <p className='flex items-start gap-2'>
                <CircleAlert className='mt-0.5 size-4 shrink-0 text-destructive' aria-hidden='true' />
                {message}
            </p>

            <a
                href={whatsappLink("Hello Loveth Tasty, I tried to send a request on your website but it didn't go through. Can you help?")}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex shrink-0 items-center gap-2 font-semibold text-brand-ember underline-offset-4 hover:underline'
            >
                <WhatsappIcon className='size-4' />
                Message us instead
            </a>
        </div>
    )
}
